// frontend/src/pages/MonitoringPage.js
import React, { useState, useEffect, useCallback } from 'react';
import useApi from '../hooks/useApi';

const MonitoringPage = () => {
    const { apiFetch } = useApi();
    
    const [logs, setLogs] = useState([]);
    const [usuarios, setUsuarios] = useState([]);
    const [comparativo, setComparativo] = useState([]);
    
    const [periodo, setPeriodo] = useState('diario'); 
    const [dataFiltro, setDataFiltro] = useState(new Date().toISOString().split('T')[0]);
    const [usuarioSelecionado, setUsuarioSelecionado] = useState('todos');
    const [loading, setLoading] = useState(false);

    // NOVO ESTADO: Usuários logados no portal web
    const [webOnlineUsers, setWebOnlineUsers] = useState([]);

    // NOVO EFEITO: Verifica quem está online no portal web (Atualiza a cada 10s)
    useEffect(() => {
        const fetchOnlineUsers = async () => {
            try {
                // Apenas puxa a lista de quem está online (O Ping agora é global no App.js)
                const res = await apiFetch('/monitor/web-online');
                if (Array.isArray(res)) setWebOnlineUsers(res);
            } catch (e) {
                console.error("Erro ao buscar usuários web online:", e);
            }
        };

        fetchOnlineUsers();
        // Checa a cada 10 segundos para dar a sensação de Tempo Real para o Gestor
        const interval = setInterval(fetchOnlineUsers, 10000);
        return () => clearInterval(interval);
    }, [apiFetch]);

    const fetchUsuarios = useCallback(async () => {
        try {
            const data = await apiFetch('/monitor/users');
            setUsuarios(data || []);
        } catch (e) { console.error("Erro ao buscar usuários:", e); }
    }, [apiFetch]);

    const fetchDashboardData = useCallback(async () => {
        setLoading(true);
        try {
            const query = new URLSearchParams({
                periodo,
                data: dataFiltro,
                usuario_id: usuarioSelecionado
            }).toString();

            const response = await apiFetch(`/monitor/dashboard?${query}`);
            
            setLogs(response.logs || []);
            setComparativo(response.comparativo || []);
        } catch (e) { 
            console.error("Erro ao buscar dados do dashboard:", e); 
        } finally {
            setLoading(false);
        }
    }, [apiFetch, periodo, dataFiltro, usuarioSelecionado]);

    useEffect(() => {
        fetchUsuarios();
    }, [fetchUsuarios]);

    useEffect(() => {
        fetchDashboardData();
    }, [fetchDashboardData]);

    const calcularPorcentagem = (horas) => {
        const meta = periodo === 'diario' ? 8 : periodo === 'mes' ? 160 : 1920; 
        const calc = (horas / meta) * 100;
        return calc > 100 ? 100 : calc;
    };

    // --- LÓGICA DO GRÁFICO DE PIZZA (DONUT) ---
    const totalLogs = logs.filter(l => l.status_atividade === 'Produtivo' || l.status_atividade === 'Improdutivo' || l.status_atividade === 'Fora do Horário').length;
    const produtivoCount = logs.filter(l => l.status_atividade === 'Produtivo').length;
    const improdutivoCount = logs.filter(l => l.status_atividade === 'Improdutivo').length;
    const foraCount = logs.filter(l => l.status_atividade === 'Fora do Horário').length;

    const produtivoHoras = (produtivoCount / 60).toFixed(2);
    const improdutivoHoras = (improdutivoCount / 60).toFixed(2);
    const foraHoras = (foraCount / 60).toFixed(2);

    const prodPct = totalLogs > 0 ? (produtivoCount / totalLogs) * 100 : 0;
    const impPct = totalLogs > 0 ? (improdutivoCount / totalLogs) * 100 : 0;
    const foraPct = totalLogs > 0 ? (foraCount / totalLogs) * 100 : 0;

    const pieGradient = totalLogs > 0 
        ? `conic-gradient(#16a34a 0% ${prodPct}%, #dc2626 ${prodPct}% ${prodPct + impPct}%, #eab308 ${prodPct + impPct}% 100%)`
        : `conic-gradient(#e5e7eb 0% 100%)`;

    return (
        <div className="p-6 bg-gray-50 min-h-screen">
            <h1 className="text-2xl font-bold mb-6 text-gray-800">Dashboard de Produtividade</h1>
            
            {/* PAINEL: USUÁRIOS ONLINE NO PORTAL */}
            <div className="bg-white p-5 rounded-lg shadow-sm border border-gray-200 mb-6 print:hidden">
                <div className="flex items-center gap-2 mb-3 border-b border-gray-100 pb-3">
                    <div className="w-3 h-3 bg-green-500 rounded-full animate-pulse shadow-[0_0_8px_rgba(34,197,94,0.6)]"></div>
                    <h2 className="text-lg font-bold text-gray-800">Usuários Online no DFinAI Web</h2>
                    <span className="bg-green-100 text-green-800 text-xs font-bold px-2.5 py-0.5 rounded-full ml-2">
                        {webOnlineUsers.length}
                    </span>
                </div>
                <div className="flex flex-wrap gap-3">
                    {webOnlineUsers.length > 0 ? (
                        webOnlineUsers.map(u => (
                            <div key={u.id} className="flex items-center gap-2 bg-gray-50 border border-gray-200 px-3 py-1.5 rounded-full shadow-sm hover:shadow-md transition-shadow cursor-default">
                                <div className="w-7 h-7 bg-blue-100 text-blue-700 rounded-full flex items-center justify-center font-black text-xs">
                                    {(u.name || u.username).substring(0, 2).toUpperCase()}
                                </div>
                                <span className="text-sm font-bold text-gray-700">{u.name || u.username}</span>
                            </div>
                        ))
                    ) : (
                        <p className="text-sm text-gray-500 italic px-2">Nenhum usuário ativo no portal neste momento.</p>
                    )}
                </div>
            </div>

            <div className="bg-white p-4 rounded-lg shadow border mb-6 flex flex-wrap gap-4 items-end">
                <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Período</label>
                    <select 
                        value={periodo} 
                        onChange={(e) => setPeriodo(e.target.value)}
                        className="w-full border-gray-300 rounded-md shadow-sm p-2 border focus:ring-indigo-500 focus:border-indigo-500"
                    >
                        <option value="diario">Diário</option>
                        <option value="mes">Mensal</option>
                        <option value="anual">Anual</option>
                    </select>
                </div>

                <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Data de Referência</label>
                    <input 
                        type={periodo === 'mes' ? 'month' : 'date'} 
                        value={dataFiltro}
                        onChange={(e) => setDataFiltro(e.target.value)}
                        className="w-full border-gray-300 rounded-md shadow-sm p-2 border focus:ring-indigo-500 focus:border-indigo-500"
                    />
                </div>

                <div className="flex-1 min-w-[200px]">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Funcionário</label>
                    <select 
                        value={usuarioSelecionado} 
                        onChange={(e) => setUsuarioSelecionado(e.target.value)}
                        className="w-full border-gray-300 rounded-md shadow-sm p-2 border focus:ring-indigo-500 focus:border-indigo-500"
                    >
                        <option value="todos">Todos da Minha Entidade</option>
                        {usuarios.map(u => (
                            <option key={u.id} value={u.id}>{u.nome_funcionario || u.login}</option>
                        ))}
                    </select>
                </div>
            </div>

            {loading && <div className="text-center text-indigo-600 mb-4 animate-pulse">Atualizando painel...</div>}

            {/* SEÇÃO NOVA: Gráfico de Pizza (Resumo Geral) */}
            <div className="bg-white p-6 rounded-lg shadow border mb-6 flex flex-col md:flex-row items-center gap-8">
                <div className="flex-1 w-full">
                    <h2 className="text-lg font-bold mb-4 text-gray-800">Distribuição de Tempo (Geral)</h2>
                    {totalLogs === 0 && !loading ? (
                         <p className="text-gray-500 text-sm">Nenhum dado registrado neste período para gerar o gráfico.</p>
                    ) : (
                        <div className="space-y-4">
                            <div className="flex items-center justify-between border-b pb-2">
                                <div className="flex items-center gap-3">
                                    <div className="w-4 h-4 bg-green-600 rounded-full shadow-sm"></div>
                                    <span className="text-sm font-semibold text-gray-700">Horas Produtivas</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{produtivoHoras}h <span className="text-gray-500 font-normal ml-1">({prodPct.toFixed(1)}%)</span></span>
                            </div>
                            <div className="flex items-center justify-between border-b pb-2">
                                <div className="flex items-center gap-3">
                                    <div className="w-4 h-4 bg-red-600 rounded-full shadow-sm"></div>
                                    <span className="text-sm font-semibold text-gray-700">Horas Improdutivas</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{improdutivoHoras}h <span className="text-gray-500 font-normal ml-1">({impPct.toFixed(1)}%)</span></span>
                            </div>
                            <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                    <div className="w-4 h-4 bg-yellow-500 rounded-full shadow-sm"></div>
                                    <span className="text-sm font-semibold text-gray-700">Fora do Horário</span>
                                </div>
                                <span className="text-sm font-bold text-gray-900">{foraHoras}h <span className="text-gray-500 font-normal ml-1">({foraPct.toFixed(1)}%)</span></span>
                            </div>
                        </div>
                    )}
                </div>
                
                {/* Gráfico Visual */}
                <div className="flex justify-center items-center p-4">
                    <div 
                        className="w-48 h-48 rounded-full shadow-md border-4 border-white relative flex items-center justify-center transition-all duration-500"
                        style={{ background: pieGradient }}
                    >
                        {/* Furo da pizza para ficar moderno (Donut Chart) */}
                        <div className="w-24 h-24 bg-white rounded-full shadow-inner flex flex-col items-center justify-center">
                            <span className="text-xs text-gray-500 font-semibold uppercase tracking-wider">Total</span>
                            <span className="text-sm font-bold text-gray-800">{((produtivoCount + improdutivoCount + foraCount) / 60).toFixed(1)}h</span>
                        </div>
                    </div>
                </div>
            </div>

            {usuarioSelecionado === 'todos' && (
                <div className="bg-white p-6 rounded-lg shadow border mb-6">
                    <h2 className="text-lg font-bold mb-4 text-gray-800">Comparativo de Horas: Produtivas vs Improdutivas</h2>
                    <div className="space-y-4">
                        {comparativo.length === 0 && !loading ? (
                            <p className="text-gray-500 text-sm">Nenhum dado produtivo registrado neste período.</p>
                        ) : (
                            comparativo.map((comp, i) => (
                                <div key={i} className="flex flex-col">
                                    <div className="flex justify-between items-end text-sm mb-1">
                                        <span className="font-semibold text-gray-800">{comp.nome_funcionario}</span>
                                        <span className="flex items-center gap-3 text-gray-600">
                                            {/* Contador de Logins e Logouts */}
                                            <span className="text-blue-600 font-semibold text-xs border-r pr-3 border-gray-300">
                                                {comp.qtd_logins} Login(s) | {comp.qtd_logouts} Logout(s)
                                            </span>
                                            <span>
                                                <span className="text-green-600 font-bold">{comp.horas_produtivas}h Prod</span> | 
                                                <span className="text-red-500 font-bold ml-1">{comp.horas_improdutivas}h Imp</span>
                                            </span>
                                        </span>
                                    </div>
                                    <div className="w-full bg-gray-200 rounded-full h-4 flex overflow-hidden shadow-inner">
                                        <div 
                                            className="bg-green-600 h-4 transition-all duration-500" 
                                            style={{ width: `${calcularPorcentagem(comp.horas_produtivas)}%` }}
                                            title="Horas Produtivas"
                                        ></div>
                                        <div 
                                            className="bg-red-500 h-4 transition-all duration-500 opacity-90" 
                                            style={{ width: `${calcularPorcentagem(comp.horas_improdutivas)}%` }}
                                            title="Horas Improdutivas"
                                        ></div>
                                    </div>
                                </div>
                            ))
                        )}
                    </div>
                </div>
            )}

            <div className="bg-white rounded-lg shadow border overflow-hidden">
                <div className="p-4 border-b bg-gray-50 flex justify-between items-center">
                    <h2 className="text-lg font-bold text-gray-800">Histórico de Atividades Detalhado</h2>
                </div>
                <div className="overflow-x-auto">
                    <table className="w-full text-left">
                        <thead className="bg-gray-100 border-b text-gray-600 text-sm">
                            <tr>
                                <th className="px-6 py-3 font-semibold">Funcionário</th>
                                <th className="px-6 py-3 font-semibold">Janela / Atividade</th>
                                <th className="px-6 py-3 font-semibold text-right">Data/Hora Real</th>
                                <th className="px-6 py-3 font-semibold text-center">Classificação</th>
                            </tr>
                        </thead>
                        <tbody>
                            {logs.length === 0 && !loading ? (
                                <tr>
                                    <td colSpan="4" className="px-6 py-8 text-center text-gray-500">
                                        Nenhuma atividade registrada para os filtros selecionados.
                                    </td>
                                </tr>
                            ) : (
                                logs.map((log, i) => (
                                    <tr key={i} className="border-b hover:bg-gray-50 transition-colors">
                                        <td className="px-6 py-4 font-bold text-gray-800">{log.nome_funcionario}</td>
                                        <td className="px-6 py-4 text-sm text-gray-700">{log.janela_ativa}</td>
                                        <td className="px-6 py-4 text-right font-mono text-sm text-gray-500">{log.hora_registro}</td>
                                        <td className="px-6 py-4 text-center">
                                            <span className={`px-2 py-1 text-xs font-semibold rounded-full ${
                                                log.status_atividade === 'Produtivo' ? 'bg-green-100 text-green-800' : 
                                                log.status_atividade === 'Improdutivo' ? 'bg-red-100 text-red-800' : 
                                                log.status_atividade === 'Acesso / Login' ? 'bg-blue-100 text-blue-800' : 
                                                log.status_atividade === 'Saída / Logout' ? 'bg-gray-200 text-gray-800' : 
                                                'bg-yellow-100 text-yellow-800'
                                            }`}>
                                                {log.status_atividade}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
};

export default MonitoringPage;